import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth";
import { fetchPostMeta } from "../api/posts";
import { createPortalSession } from "../api/subscriptions";

export default function Layout({ children }) {
  const { user, subscription, supportAccount, supportAvailable, endSupport } = useAuth();
  const [postKinds, setPostKinds] = useState([]);
  const [portalLoading, setPortalLoading] = useState(false);
  const isPro = String(subscription?.plan?.name || "").trim().toLowerCase() === "pro";
  const isFree = String(subscription?.plan?.name || "").trim().toLowerCase() === "free";

  const openSubscriptionPortal = async () => {
    if (!user || portalLoading) return;
    const portalWindow = window.open('about:blank', '_blank');
    if (!portalWindow) {
      window.alert('Please allow pop-ups so the subscription portal can open in a new tab.');
      return;
    }
    portalWindow.opener = null;
    setPortalLoading(true);
    try {
      const response = await createPortalSession({
        uid: user.uid,
        returnUrl: `${window.location.origin}/posts/attraction`
      });
      const url = response?.result?.url;
      if (!url) throw new Error('Stripe portal URL not returned');
      portalWindow.location.replace(url);
    } catch (error) {
      portalWindow.close();
      window.alert(error?.message || 'Unable to open the subscription portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    if (!user) {
      setPostKinds([]);
      return () => { active = false; };
    }
    fetchPostMeta()
      .then(meta => {
        if (!active) return;
        const planKinds = subscription?.plan?.postKinds || {};
        setPostKinds(Object.entries(meta?.kinds || {})
          .filter(([kind]) => {
            if (kind === "profile" || kind === "attraction") return true;
            if (isPro && (kind === "resource" || kind === "policy")) return true;
            const planKind = planKinds[kind];
            return Boolean(planKind) &&
              planKind.enabled !== false &&
              (planKind.limit == null || Number(planKind.limit) !== 0);
          })
          .map(([kind, config]) => ({
            kind,
            label: config?.label || kind
          })));
      })
      .catch(() => active && setPostKinds([]));
    return () => { active = false; };
  }, [user, subscription, isPro]);

  return (
    <div className="d-flex min-vh-100 flex-column">
      {supportAccount && (
        <div className="bg-warning text-dark py-2">
          <div className="container d-flex justify-content-between align-items-center gap-3">
            <strong>Managing {supportAccount.domain || supportAccount.companyName || supportAccount.id} as Super Admin</strong>
            <button className="btn btn-sm btn-dark" type="button" onClick={endSupport}>Exit account</button>
          </div>
        </div>
      )}
      <nav className="border-bottom py-3">
        <div className="container d-flex justify-content-center gap-3">
          {!user ? (
            <>
              <Link to="/contact" className="text-decoration-none">Contact</Link>
              <Link to="/login" className="text-decoration-none">Login</Link>
            </>
          ) : (
            <>
              <div className="dropdown">
                <button
                  className="btn btn-link dropdown-toggle text-decoration-none p-0"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Content
                </button>
                <ul className="dropdown-menu">
                  {postKinds.map(({ kind, label }) => (
                    <li key={kind}>
                      <Link className="dropdown-item" to={`/posts/${kind}`}>{label}</Link>
                    </li>
                  ))}
                  {!postKinds.length && <li><span className="dropdown-item-text text-muted">No content available</span></li>}
                </ul>
              </div>
              <Link to="/leads" className="text-decoration-none">Leads</Link>
              {supportAvailable && <Link to="/support" className="text-decoration-none">Support</Link>}
              {!supportAccount && (isFree ? (
                <a href="https://bouncehouse.com/businesses#pricing" className="text-decoration-none">Upgrade</a>
              ) : (
                <button
                  type="button"
                  className="btn btn-link text-decoration-none p-0"
                  onClick={openSubscriptionPortal}
                  disabled={portalLoading}
                >
                  {portalLoading ? 'Opening...' : 'Subscription'}
                </button>
              ))}
              {isPro && <Link to="/website" className="text-decoration-none">Website</Link>}
              <Link to="/contact" className="text-decoration-none">Contact</Link>
              <Link to="/logout" className="text-decoration-none">Logout</Link>
            </>
          )}
        </div>
      </nav>

      <main className="container py-5 flex-grow-1">
        {children}
      </main>

      <footer className="site-footer mt-auto">
        <div className="container py-5">
          <div className="row g-4 align-items-start">
            <div className="col-12 col-lg-8">
              <a href="https://bouncehouse.com" target="_blank" rel="noreferrer">
                <img
                  src="https://ik.imagekit.io/switchbaq/bouncehouse/bouncehouse_logo.png"
                  alt="BounceHouse.com"
                  className="site-footer-logo"
                />
              </a>
              <p className="site-footer-copy mt-3 mb-0">
                Helping families discover party rentals and helping local rental businesses grow.
              </p>
            </div>

            <div className="col-12 col-sm-6 col-lg-3 ms-lg-auto">
              <h2 className="site-footer-heading">Contact</h2>
              <ul className="site-footer-links">
                <li><Link to="/contact">Contact us</Link></li>
                <li><a href="mailto:help@bouncehouse.com">Email support</a></li>
              </ul>
            </div>
          </div>

          <div className="site-footer-bottom d-flex flex-column flex-md-row justify-content-between gap-3 mt-5 pt-4">
            <span>© {new Date().getFullYear()} BounceHouse.com LLC</span>
            <div className="d-flex flex-wrap gap-3">
              <a href="https://bouncehouse.com/policies/privacy">Privacy</a>
              <a href="https://bouncehouse.com/policies/terms">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
